import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Header, Label, Loader, Text, Stack } from '@crossfit/ui'
import cx from 'classnames'
import type { Image } from 'types'
import * as contentService from 'services/content'
import InnerContent from 'components/InnerContent'
import TermsOfService from 'components/TermsOfService'
import PartnerList, { PartnerItem } from './PartnerList'
import style from './style.module.css'
import usePageTitle from 'hooks/usePageTitle'
import NotAuthorized from 'components/NotAuthorized'
import AffiliateId from './AffiliateId'
import Toggle from './Toggle'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'
import { ToggleValue } from 'components/Admin/Shared/Toggle'
import { useProductAccess } from 'hooks/useProductAccess'
import { SubscriptionPackageType } from 'services/programming'

type Partner = {
  headlineText: string
  bodyText: string
  category: string
  image: Image
  countryEligibility: string[]
  unavailableLink: {
    text: string
    url: string
  }
  link: {
    text: string
    url: string
  }
}

const isPartnerEligible = (
  affiliateCountryCode: string,
  partner: Partner,
  productAccess: Set<SubscriptionPackageType>,
) => {
  return (
    partner.countryEligibility.includes(affiliateCountryCode) ||
    partner.countryEligibility.includes('ALL') ||
    productAccess.has(SubscriptionPackageType.ATK_STARTER)
  )
}

type Content = {
  headlineText: string
  subheadlineText: string
  browseText: string
  availablePartnerDealsText: string
  notCurrentlyAvailableText: string
  showUnavailableDealsText: string
  affiliateIdTooltip: {
    headlineText: string
    descriptionText: string
  }
  partners: Partner[]
}

function usePartnerNetwork() {
  const { canAccessPartnerNetwork, selectedAffiliate: affiliate } =
    useAffiliate()
  const productAccess = useProductAccess()
  const contentQuery = useQuery(
    'affiliate-partner-network',
    () => contentService.fetchPage<Content>('/affiliate-partner-network'),
    { enabled: canAccessPartnerNetwork },
  )

  const { availablePartners, unavailablePartners } = useMemo(() => {
    if (!contentQuery.data?.partners) {
      return { availablePartners: [], unavailablePartners: [] }
    }

    const availablePartners = contentQuery.data.partners.filter((partner) =>
      isPartnerEligible(
        affiliate?.affiliateCountryCode,
        partner,
        productAccess,
      ),
    )
    const unavailablePartners = contentQuery.data.partners.filter(
      (partner) =>
        !isPartnerEligible(
          affiliate?.affiliateCountryCode,
          partner,
          productAccess,
        ),
    )
    return { availablePartners, unavailablePartners: unavailablePartners }
  }, [contentQuery.data?.partners, affiliate?.affiliateCountryCode])

  return {
    affiliateId: affiliate?.id,
    affiliateCountryCode: affiliate?.affiliateCountryCode,
    content: { ...contentQuery.data, availablePartners, unavailablePartners },
    isLoading: contentQuery.isLoading,
    hasAccess: canAccessPartnerNetwork,
  }
}

function PartnerNetworkPage() {
  const { hasValidAgreement, selectedAffiliate } = useAffiliate()
  const { content, isLoading, hasAccess, affiliateId } = usePartnerNetwork()
  usePageTitle(
    `CrossFit Affiliate Toolkit${content ? ` | ${content.headlineText}` : ''}`,
  )

  const [showUnavailablePartners, setShowUnavailablePartners] = useState(false)

  if (isLoading) {
    return <Loader />
  }
  if (!hasAccess) {
    return <NotAuthorized />
  }

  return (
    <>
      {hasValidAgreement ? (
        <>
          <header className={style.header}>
            <InnerContent variant='xlarge' className={style.innerContent}>
              <Header size='large' as='h2' mb={3}>
                {content.headlineText}
              </Header>
              <Text className={style.subheadlineText}>
                {content.subheadlineText}
              </Text>
            </InnerContent>
          </header>
          <div
            className={cx(style.toolbar, {
              [style.noToggle]: content.unavailablePartners?.length === 0,
              [style.hide]: affiliateId == null,
            })}
          >
            <InnerContent variant='xlarge' className={style.innerContent}>
              {content.unavailablePartners.length > 0 && affiliateId && (
                <Stack
                  direction='horizontal'
                  spacing={4}
                  className={style.toggle}
                >
                  <Label htmlFor='partner-toggle' className={style.label}>
                    {content.showUnavailableDealsText}
                  </Label>
                  <Toggle
                    id='partner-toggle'
                    onChange={() => {
                      trackEvent({
                        event: 'Unavailable Deals Toggled',
                        component: 'Partner Network Page',
                        location: 'Affiliate Toolkit',
                        text: showUnavailablePartners ? 'Hide' : 'Show',
                        affiliateId: selectedAffiliate?.affiliateId,
                        userRole:
                          selectedAffiliate?.roles?.length > 0
                            ? selectedAffiliate?.roles[0]
                            : null,
                        groupId: selectedAffiliate?.groupId,
                      })
                      setShowUnavailablePartners((v) => !v)
                    }}
                    state={
                      showUnavailablePartners
                        ? ToggleValue.PUBLIC
                        : ToggleValue.PRIVATE
                    }
                  />
                </Stack>
              )}
              {affiliateId ? (
                <AffiliateId
                  affiliateId={affiliateId}
                  className={style.affiliateId}
                  tooltip={content.affiliateIdTooltip}
                />
              ) : (
                <></>
              )}
            </InnerContent>
          </div>
          <main className={style.main}>
            <PartnerList headerText={content.availablePartnerDealsText}>
              {content.availablePartners.map((partner) => (
                <PartnerItem
                  partner={partner}
                  key={partner.headlineText}
                  unavailable={false}
                />
              ))}
            </PartnerList>
            {content.unavailablePartners.length > 0 && showUnavailablePartners && (
              <PartnerList headerText={content.notCurrentlyAvailableText}>
                {content.unavailablePartners.map((partner) => (
                  <PartnerItem
                    partner={partner}
                    key={partner.headlineText}
                    unavailable
                  />
                ))}
              </PartnerList>
            )}
          </main>
        </>
      ) : (
        <TermsOfService />
      )}
    </>
  )
}

export default PartnerNetworkPage
